import { Badge } from '../../components/ui';
import { MISSING } from '../dashboard/format';
import { cabinetChaseView, type ChaseRow } from './cabinet-model';

/**
 * ADDITIVE (v1.9.64, T.Z195) — the four chase cells of a cabinet children row:
 * «Договор», «Дедлайн договора», «Зачислен», «Не перешёл». ONE component for BOTH
 * children tables (the offline roster and the online set), so the two can never drift;
 * the views inside are the REGISTER's (`cabinetChaseView` folds the api's facts with
 * the register's own helpers), the chrome is the `<Badge>` the register's cells pill
 * with. NO signer identity: the api never sends `signerName` to a curator surface.
 *
 * The quiet rules travel with the register's: no fact renders «—», never a guess; a
 * signed row's deadline cell is the calm signing day, not a second green pill; the
 * «Не перешёл» column keeps the register's «Провалил ЕВИ» label (no term drift) and
 * its tooltip maps the curators' word onto it.
 */
export function ChaseCells({ row, id }: { row: ChaseRow; /** Row-unique suffix for the cell testids. */ id: string | number }) {
  const chase = cabinetChaseView(row);
  return (
    <>
      <td data-testid={`chase-contract-${id}`}>
        {chase.contract === null ? (
          <span className="cell-muted">{MISSING}</span>
        ) : (
          <Badge tone={chase.contract.tone} title="Договор на платформе iSmart">
            {chase.contract.label}
          </Badge>
        )}
      </td>
      <td data-testid={`chase-deadline-${id}`}>
        {chase.deadline === null ? (
          <span className="cell-muted">{MISSING}</span>
        ) : chase.deadline.tone === 'signed' ? (
          <span className="cell-muted" title="Договор подписан — дедлайна больше нет">
            {chase.deadline.label}
          </span>
        ) : (
          <Badge tone={chase.deadline.tone === 'danger' ? 'bad' : 'warn'} title={chase.deadlineTitle}>
            {chase.deadline.label}
          </Badge>
        )}
      </td>
      <td data-testid={`chase-enrolled-${id}`}>
        {chase.enrolled.label === '—' ? (
          <span className="cell-muted" title={chase.enrolled.title}>
            {chase.enrolled.label}
          </span>
        ) : chase.enrolled.tone === 'ok' ? (
          <Badge tone="ok" title={chase.enrolled.title}>
            {chase.enrolled.label}
          </Badge>
        ) : (
          <span className="cell-muted" title={chase.enrolled.title}>
            {chase.enrolled.label}
          </span>
        )}
      </td>
      <td data-testid={`chase-evi-failed-${id}`}>
        {chase.eviFailed === null ? (
          <span className="cell-muted">{MISSING}</span>
        ) : (
          <Badge tone="bad" title={chase.eviFailed.title}>
            {chase.eviFailed.label}
          </Badge>
        )}
      </td>
    </>
  );
}

/** The four column captions, in the register's own order and wording. */
export function ChaseColumnHeaders() {
  return (
    <>
      {/* ADDITIVE (v1.9.64, T.Z195) — the register's «Договор» + «Дедлайн договора» pair. */}
      <th scope="col">Договор</th>
      <th scope="col">Дедлайн договора</th>
      {/* ADDITIVE (v1.9.52/T.Z176's word) — the enrollment fact. */}
      <th scope="col">Зачислен</th>
      {/* ADDITIVE (v1.9.64, T.Z195) — the curators' word for the «Провалил ЕВИ» fact;
          the label inside the cells stays «Провалил ЕВИ», the tooltip maps the two. */}
      <th scope="col" title='Колонка «Не перешёл» — это пометка «Провалил ЕВИ»: ребёнок не перешёл на следующий этап.'>
        Не перешёл
      </th>
    </>
  );
}
